import Session from "@/utils/Session";
import BaseService from "./BaseService";
import Profile from "../utils/Profile";

const VUE_APP_BACKEND_GUEST_API = process.env.VUE_APP_BACKEND_GUEST_API;
const VUE_APP_BACKEND_API = process.env.VUE_APP_BACKEND_API;
const API_LOGIN = `${VUE_APP_BACKEND_GUEST_API}/Account/Login()`;
const API_REFRESH_TOKEN = `${VUE_APP_BACKEND_GUEST_API}/Account/RefreshToken()`;

class AuthService extends BaseService {
    /**
     * Get the user info with the session.
     *
     * @return {Promise}
     */
    static async getUserInfo() {
        if (!this.hasSession())
            return Promise.reject();

        try {
            let response = await window.axios.get(`${VUE_APP_BACKEND_API}/User/Profile`);
            return await Promise.resolve(response.data);
        } catch (e) {
            return await Promise.reject();
        }
    }

    /**
     * Get the user info without the session, but with a token.
     *
     * @param {String} token
     *
     * @return {Promise}
     */
    static async getUserInfoWithoutSession(token) {
        if (!token.trim().length)
            return Promise.reject();

        try {
            let options = {headers: {"Authorization": `Bearer ${token}`}};
            let response = await window.axios.get(`${VUE_APP_BACKEND_API}/User/Profile`, options);
            return response.data;
        } catch (e) {
            return await Promise.reject();
        }
    }

    /**
     * Refresh the access token using the refresh token.
     *
     * @return {Promise}
     */
    static async refreshAccessToken() {
        if (!this.hasSession())
            return Promise.reject();

        let refreshRequestBody = {
            RefreshToken: Session.refreshToken.get()
        }

        try {
            let response = await window.axios.post(API_REFRESH_TOKEN, refreshRequestBody);
            Session.set(response.data);
            return await Promise.resolve();
        } catch (e) {
            Session.delete();
            return await Promise.reject();
        }
    }

    /**
     * Checks if a user is authenticated. If token is invalid,
     * try using the refresh token before rejecting.
     *
     * @return {Promise}
     */
    static check() {
        return new Promise((resolve, reject) => {
            this.getUserInfo()
                .then(() => resolve())
                .catch(() => {
                    this.refreshAccessToken()
                        .then(() => resolve())
                        .catch(() => reject());
                });
        });
    }

    /**
     * Login a user by his username and password.
     * Not using the window.axios since authorization is not needed.
     *
     * @param {string} username
     * @param {string} password
     *
     * @return {Promise}
     */
    static login(username, password) {
        let loginBody = {
            Username: username,
            Password: password
        }

        if(this.hasSession()) {
            Session.delete();
            Profile.delete();
        }

        return window.axios.post(API_LOGIN, loginBody);
    }

    static activate(token, id) {
        let body = {
            "Id": id,
            "Token": token
        };
        let url = `${VUE_APP_BACKEND_GUEST_API}/Account/VerifyEmail()`;
        return window.axios.post(url, body);
    }

    static resetPassword(token, id, password) {
        let body = {
            "KeycloakId": id,
            "Token": token,
            "Password": password
        };
        let url = `${VUE_APP_BACKEND_GUEST_API}/User/ResetPassword()`;
        return window.axios.post(url, body);
    }

    static forgotPassword(username) {
        let body = {
            "Username": username
        };
        let url = `${VUE_APP_BACKEND_GUEST_API}/Account/ForgetPassword()`;
        return window.axios.post(url, body);
    }

    /**
     * Logout a user.
     *
     * @return {Promise}
     */
    static async logout() {
        if (!this.hasSession())
            return Promise.reject();

        Profile.delete();
        Session.delete();
        localStorage.clear();

        let body = new URLSearchParams();

        body.append('refreshToken', Session.refreshToken.get());

        try {
            await window.axios.post(`${VUE_APP_BACKEND_API}/Account/Logout()`, body);
            console.log('logging out');
            return await Promise.resolve();
        } catch (e) {
            return await Promise.reject();
        }
    }
}

export default AuthService;
